// src/pages/Rentas.jsx
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

// Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function Rentas() {
  const [reservas, setReservas] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [groupBy, setGroupBy] = useState('month');

  useEffect(() => {
    // Obtener reservas
    axios
      .get('https://api.jsgraceinvestmentsllc.com/api/reservas')
      .then(res => setReservas(res.data))
      .catch(err => console.error('Error al obtener las reservas:', err));

    // Obtener vehículos
    axios
      .get('https://api.jsgraceinvestmentsllc.com/api/vehicles')
      .then(res => setVehicles(res.data))
      .catch(err => console.error('Error al obtener los vehículos:', err));
  }, []);

  // Traducir tipo de reserva
  const traducirTipo = tipo => {
    if (tipo === 'C') return 'Vehículo';
    if (tipo === 'V') return 'Alojamiento';
    if (tipo === 'E') return 'Excursión';
    return tipo;
  };

  // Obtener nombre de vehículo
  const nombreVehiculo = vehiculoId => {
    const v = vehicles.find(x => x.id === Number(vehiculoId));
    return v ? `${v.marca} ${v.modelo}` : '—';
  };

  // Conteos por tipo
  const conteos = useMemo(() => {
    const counts = { C: 0, V: 0, E: 0 };
    reservas.forEach(r => {
      if (counts.hasOwnProperty(r.tipo)) counts[r.tipo]++;
    });
    return counts;
  }, [reservas]);

  // Agrupación por mes o día
  const agrupado = useMemo(() => {
    const conteo = {};
    reservas.forEach(r => {
      const d = new Date(r.fecha_inicio);
      const key =
        groupBy === 'month'
          ? d.toLocaleString('es-ES', { year: 'numeric', month: 'long' })
          : r.fecha_inicio;
      conteo[key] = (conteo[key] || 0) + 1;
    });
    return Object.keys(conteo)
      .sort((a, b) => new Date(a) - new Date(b))
      .map(k => ({ label: k, count: conteo[k] }));
  }, [reservas, groupBy]);

  // Datos del gráfico
  const dataChart = {
    labels: agrupado.map(i => i.label),
    datasets: [
      {
        label: 'Reservas',
        data: agrupado.map(i => i.count),
        backgroundColor: 'rgba(13, 110, 253, 0.5)',
      },
    ],
  };
  const optionsChart = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: `Reservaciones por ${groupBy === 'month' ? 'Mes' : 'Día'}`,
      },
    },
  };

  // Cálculos rápidos
  const hoy = new Date().toISOString().slice(0, 10);
  const reservasHoy = reservas.filter(r => r.fecha_inicio === hoy).length;
  const totalVehiculos = conteos.C;
  const totalAlojamientos = conteos.V;
  const totalExcursiones = conteos.E;

  // Eliminar reserva usando SweetAlert2
  const handleDelete = id => {
    Swal.fire({
      title: '¿Eliminar esta reserva?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (!result.isConfirmed) return;
      axios
        .delete(`https://api.jsgraceinvestmentsllc.com/api/reservas/${id}`)
        .then(res => {
          if (res.data.message === 'Reserva eliminada correctamente') {
            setReservas(prev => prev.filter(r => r.id !== id));
            Swal.fire('Eliminada', 'La reserva ha sido eliminada.', 'success');
          } else {
            Swal.fire('Error', res.data.error || 'No se pudo eliminar la reserva.', 'error');
          }
        })
        .catch(err => {
          console.error('Error al eliminar reserva:', err);
          Swal.fire('Error', 'Error comunicándose con el servidor.', 'error');
        });
    });
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      {/* Tarjeta de bienvenida */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card p-4">
            <h5 className="text-primary">¡Bienvenido administrador! 🎉</h5>
            <p>Aquí tienes un resumen de las reservas y estadísticas clave.</p>
          </div>
        </div>
      </div>

      {/* Gráfico y conteos */}
      <div className="row mb-4">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="m-0">
                Reservaciones por {groupBy === 'month' ? 'Mes' : 'Día'}
              </h5>
              <div className="btn-group btn-group-sm">
                <button
                  className={`btn ${groupBy === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setGroupBy('month')}
                >
                  Mes
                </button>
                <button
                  className={`btn ${groupBy === 'day' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setGroupBy('day')}
                >
                  Día
                </button>
              </div>
            </div>
            <div className="card-body">
              <Bar data={dataChart} options={optionsChart} />
              <hr />
              <div className="table-responsive text-nowrap">
                <table className="table table-striped mb-0">
                  <thead>
                    <tr>
                      <th>{groupBy === 'month' ? 'Mes' : 'Día'}</th>
                      <th>Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agrupado.map(item => (
                      <tr key={item.label}>
                        <td>{item.label}</td>
                        <td>{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="row">
            <div className="col-md-4 mb-3">
              <div className="card text-center p-3 h-100">
                <div className="mb-2">
                  <i className="bx bx-calendar-event fs-1 text-primary"></i>
                </div>
                <h6 className="mb-2">Hoy</h6>
                <h3>{reservasHoy}</h3>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="card text-center p-3 h-100">
                <div className="mb-2">
                  <i className="bx bx-car fs-1 text-success"></i>
                </div>
                <h6 className="mb-2">Vehículo</h6>
                <h3>{totalVehiculos}</h3>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="card text-center p-3 h-100">
                <div className="mb-2">
                  <i className="bx bx-home fs-1 text-warning"></i>
                </div>
                <h6 className="mb-2">Alojamiento</h6>
                <h3>{totalAlojamientos}</h3>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="card text-center p-3 h-100">
                <div className="mb-2">
                  <i className="bx bx-map-pin fs-1 text-info"></i>
                </div>
                <h6 className="mb-2">Excursión</h6>
                <h3>{totalExcursiones}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de reservas */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <h5 className="card-header">Listado de Reservas</h5>
            <div className="table-responsive text-nowrap">
              <table className="table table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Tipo</th>
                    <th>Nombre</th>
                    <th>Correo</th>
                    <th>Teléfono</th>
                    <th>Vehículo</th>
                    <th>Alojamiento</th>
                    <th>Excursión</th>
                    <th>Precio</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Entrega</th>
                    <th>Devolución</th>
                    <th>Adultos</th>
                    <th>Niños</th>
                    <th>Comentarios</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {reservas.map(r => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td>{traducirTipo(r.tipo)}</td>
                      <td>{r.nombre}</td>
                      <td>{r.correo || '—'}</td>
                      <td>{r.telefono || '—'}</td>
                      <td>{nombreVehiculo(r.vehiculo)}</td>
                      <td>{r.alojamiento || '—'}</td>
                      <td>{r.lugar_excursion || '—'}</td>
                      <td>{r.precio != null ? `$${parseFloat(r.precio).toFixed(2)}` : '—'}</td>
                      <td>{`${r.fecha_inicio}${r.hora_inicio ? ' ' + r.hora_inicio : ''}`}</td>
                      <td>{`${r.fecha_fin}${r.hora_fin ? ' ' + r.hora_fin : ''}`}</td>
                      <td>{r.lugar_entrega || '—'}</td>
                      <td>{r.lugar_devolucion || '—'}</td>
                      <td>{r.adultos ?? '—'}</td>
                      <td>{r.ninos ?? '—'}</td>
                      <td>{r.comentarios || '—'}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(r.id)}
                        >
                          <i className="bx bx-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Rentas;
